import { getLsParametersCookie, updateStorageCookie } from "./cookies";
import * as qs from "query-string";

interface StorageData {
	[key: string]: string;
}

/**
 * Classe destinada a contornar a possibilidade de que o usuário
 * desabilite o localStorage, reproduzindo o seu comportamento em memória.
 */

export class CustomStorage {
	constructor(private rawLSParametersCookieString?: string) {
		let LSParametersCookieArray;

		if (rawLSParametersCookieString) {
			LSParametersCookieArray = rawLSParametersCookieString;
		} else if (typeof window !== "undefined") {
			LSParametersCookieArray = getLsParametersCookie();
		}

		if (LSParametersCookieArray) {
			this.setStorageFromArray(Object.entries(qs.parse(LSParametersCookieArray)));
		}
	}

	private hasLocalStorage = (() => {
		try {
			const testItem = "test";
			localStorage.setItem(testItem, testItem);
			localStorage.removeItem(testItem);
			return true;
		} catch {
			return false;
		}
	})();

	private data: StorageData = {};

	public setItem(key: string, value: string, saveWithAE3Prefix = true): void {
		const keyToSave = saveWithAE3Prefix ? `AE3_${key}` : key;
		if (this.hasLocalStorage) {
			localStorage.setItem(keyToSave, value);
		} else {
			this.data[keyToSave] = value;
		}

		if (saveWithAE3Prefix) {
			updateStorageCookie();
		}
	}

	public getItem(key: string, getAE3PrefixItem = true): string | null {
		const keyToGet = getAE3PrefixItem ? `AE3_${key}` : key;
		if (this.hasLocalStorage) {
			return localStorage.getItem(keyToGet);
		} else {
			return this.data[keyToGet] ?? null;
		}
	}

	public removeItem(key: string, removeAE3PrefixItem = true): void {
		const keyToRemove = removeAE3PrefixItem ? `AE3_${key}` : key;
		if (this.hasLocalStorage) {
			localStorage.removeItem(keyToRemove);
		} else {
			delete this.data[keyToRemove];
		}

		if (removeAE3PrefixItem) {
			updateStorageCookie();
		}
	}

	public clear = this.hasLocalStorage ? localStorage.clear.bind(localStorage) : () => (this.data = {});

	public setStorageFromArray = (arrayElements: Array<[string, any]>) => {
		for (const [key, value] of arrayElements) {
			const treatedKey = key.replace("AE3_", "");
			const valuesIfBelongsToAbTest = abTestIdentifiers.find((identifier) => identifier.id === treatedKey);
			if (valuesIfBelongsToAbTest) {
				this.setItem(`${valuesIfBelongsToAbTest.key.toLocaleLowerCase()}_rf`, value);
			} else {
				this.setItem(treatedKey, value);
			}
		}
	};
}

export const storage = new CustomStorage();

interface IABTestIdentifier {
	id: string;
	key: string;
}

export const abTestIdentifiers: IABTestIdentifier[] = [
	{ id: "1", key: "askWhenWantToJoinTheCourse" },
	{ id: "2", key: "canaryAPI8421" },
	{ id: "3", key: "checkoutCouponField" },
	{ id: "4", key: "eligibilityRules" },
	{ id: "5", key: "enaeDatesLayout" },
	{ id: "6", key: "enaeHeadline" },
	{ id: "7", key: "enaeSimulado" },
	{ id: "8", key: "enaePostTestDay" },
	{ id: "9", key: "enaeResultDay" },
	{ id: "10", key: "enaeTestDay" },
	{ id: "11", key: "explainBenefitsCard" },
	{ id: "12", key: "homeMediaSession" },
	{ id: "13", key: "howItWorks" },
	{ id: "14", key: "labelsInSearch" },
	{ id: "15", key: "myInterests" },
	{ id: "16", key: "newCheckoutMarketplaceDesign" },
	{ id: "17", key: "newMyAccountPage" },
	{ id: "18", key: "newSearchEngine6" },
	{ id: "19", key: "newVersionSearchCardEnem" },
	{ id: "20", key: "numericKeyboardOnCheckout" },
	{ id: "21", key: "pix" },
	{ id: "22", key: "poll" },
	{ id: "23", key: "reclameAqui" },
	{ id: "24", key: "recommendations" },
	{ id: "25", key: "renderOfferPageForHiddenInstitutions" },
	{ id: "26", key: "score2" },
	{ id: "27", key: "scrollOnSearch" },
	{ id: "28", key: "showCourseBadgeBenefits" },
	{ id: "29", key: "showDiscountLabelV2" },
	{ id: "30", key: "showEnaeExamDate" },
	{ id: "31", key: "showFaqEnae" },
	{ id: "32", key: "showLGPDCookiesModal2" },
	{ id: "33", key: "showLGPDFirstStage2" },
	{ id: "34", key: "showLaborMarketParagraph" },
	{ id: "35", key: "showMecScore" },
	{ id: "36", key: "showNewIEPage" },
	{ id: "37", key: "showTermsInAddressStepOnCheckout" },
	{ id: "38", key: "signUpText" },
	{ id: "39", key: "testAbPhonenumberBuscaNota" },
	{ id: "40", key: "testAbTestSpliting" },
	{ id: "41", key: "abTestCoursesSelect" },
	{ id: "42", key: "whatsDialog" },
	{ id: "43", key: "h1Home" },
	{ id: "44", key: "promotionBarWithTimerBuscaNota" },
];

export function isProd() {
	if (process.env.AMIGOEDU_MODE === "production") {
		return true;
	}

	return false;
}

export function getUserEnemScore(): number | null {
	if (storage.getItem("userEnemScore")) {
		return JSON.parse(storage.getItem("userEnemScore")!).userEnemScore;
	}
	return null;
}
